// src/modules/units/tyres/shared/tyre-action-schema.js
//
// Single source of truth for tyre ACTIONS: the canonical actionType vocabulary
// and the shape of each action's free-form `details` bag. The backend stores
// `details` as an untyped object and nowhere else, so this file is the contract
// that keeps the write side (the Action Capture form) and the read side (the
// detail view's history timeline) in agreement about what lives in there and how
// it reads.
//
// Note: the dashboard ring's `register`, `stock`, `reports` and `find` are
// navigation, not tyre actions — they never produce an action-log entry, so they
// are intentionally absent here. The real lifecycle actions are below.

import { formatTread, formatMoney } from './tyre-format.js';
import {
  WEAR_PATTERNS, TYRE_DAMAGE_SEVERITIES, TYRE_REPAIR_TYPES, SERVICE_DELIVERY_OPTIONS, TYRE_GRADES
} from './tyre-vocab.js';
// Retread returns arrive as one `retread_return` action carrying details.outcome;
// resolveActionType maps that onto an outcome-specific label/tone below. Importing
// the enum here is acyclic — retread-batch-core imports nothing from this file.
import { RETREAD_OUTCOME } from './retread-batch-core.js';

export const TYRE_ACTION = Object.freeze({
  FIT: 'fit',
  REMOVE: 'remove',
  INSPECT: 'inspect',
  ROTATE: 'rotate',
  REPAIR: 'repair',
  RETREAD: 'retread',
  RETIRE: 'retire'
});

// Retiring is one decision to the user and three values to the backend: the reason
// a tyre leaves the fleet IS the enum the log stores. This table is the only place
// that mapping lives — it drives the form's choices, the actionType sent on write,
// and the label read back on the timeline, the same way detailFields drives four
// things from one definition.
//
// Quarantine is deliberately absent: it is temporary, so it is not a retirement.
export const RETIRE_REASONS = Object.freeze([
  { id: 'scrap', label: 'Scrapped', apiType: 'scrap', hint: 'Worn out or damaged beyond repair' },
  { id: 'sale', label: 'Sold', apiType: 'sale', hint: 'Sold on or traded in' },
  { id: 'missing', label: 'Missing', apiType: 'missing', hint: 'Cannot be located' }
]);

const RETIRE_REASON_BY_ID = Object.fromEntries(RETIRE_REASONS.map((r) => [r.id, r]));

export function retireReason(id) {
  return RETIRE_REASON_BY_ID[id] || null;
}

// Legacy / server-emitted strings normalised to the canonical vocabulary, so
// history rows created by the older per-wheel form (which wrote 'inspection')
// still map cleanly. Extend as we learn what the association endpoint stamps.
// Confirmed: associating a tyre with a sensor is stamped as FIT by the backend.
const ALIASES = Object.freeze({
  inspection: TYRE_ACTION.INSPECT,
  fitted: TYRE_ACTION.FIT,
  fitment: TYRE_ACTION.FIT,
  association: TYRE_ACTION.FIT,
  'sensor-association': TYRE_ACTION.FIT,
  unassociation: TYRE_ACTION.REMOVE,
  removal: TYRE_ACTION.REMOVE,
  rotation: TYRE_ACTION.ROTATE
});

// The backend's actionType enum for POST /action differs from our internal ids
// (e.g. we use `inspect`; it stores `inspection`). Map canonical → API string on
// the way out. Values not listed pass through unchanged (repair/retread already
// match). The full backend enum also includes purchase, stock, quarantine, scrap,
// sale, missing and note — lifecycle/status events we don't yet write from here.
const API_ACTION_TYPE = Object.freeze({
  [TYRE_ACTION.FIT]: 'fitment',
  [TYRE_ACTION.REMOVE]: 'unassociation',
  [TYRE_ACTION.INSPECT]: 'inspection',
  [TYRE_ACTION.ROTATE]: 'rotation',
  [TYRE_ACTION.REPAIR]: 'repair',
  [TYRE_ACTION.RETREAD]: 'retread',
  // The one action whose API type is a function of the data rather than fixed.
  // Resolving it here keeps apiActionType() the single write-side translation
  // point instead of letting the form special-case one action on its way out.
  [TYRE_ACTION.RETIRE]: (details) => retireReason(details?.retireReason)?.apiType || null
});

/**
 * The actionType string to send to the API for a given internal action.
 * `details` is that action's details bag; only retire consults it today, but it
 * is passed always so no caller has to know which actions care.
 */
export function apiActionType(actionType, details = {}) {
  const t = normalizeActionType(actionType);
  const mapped = API_ACTION_TYPE[t];
  if (typeof mapped === 'function') return mapped(details) || t;
  return mapped || t;
}

export function normalizeActionType(actionType) {
  if (!actionType) return '';
  const raw = String(actionType).trim().toLowerCase();
  return ALIASES[raw] || raw;
}

// The association endpoint stamps FIT for both attaching and clearing a sensor.
// If a "fit" record ends with no sensor but had one before, it is really a
// removal — resolve that here so the timeline reads correctly.
export function resolveActionType(actionLog = {}) {
  const t = normalizeActionType(actionLog.actionType);
  if (t === TYRE_ACTION.FIT) {
    const nowNone = actionLog.currentSensorId === null || actionLog.currentSensorId === undefined || actionLog.currentSensorId === '';
    const hadOne = !(actionLog.previousSensorId === null || actionLog.previousSensorId === undefined || actionLog.previousSensorId === '');
    if (nowNone && hadOne) return TYRE_ACTION.REMOVE;
  }
  // A single retread_return covers all three outcomes; split it here so the row
  // reads "Retreaded" (green) / "Rejected" (red) / "Written off" rather than a flat
  // "Returned". getActionDetailRows still keys off the base type, so the display
  // fields are shared. An absent/unknown outcome falls back to the base entry.
  if (t === 'retread_return') {
    switch (actionLog.details?.outcome) {
      case RETREAD_OUTCOME.RETREADED: return 'retread_return_retreaded';
      case RETREAD_OUTCOME.REJECTED: return 'retread_return_rejected';
      case RETREAD_OUTCOME.WRITTEN_OFF: return 'retread_return_written_off';
      default: break;
    }
  }
  return t;
}

// format tags map a details value to a shared formatter for display.
const FORMATTERS = {
  mm: (v) => formatTread(v),
  money: (v) => { const s = formatMoney(v); return s === '—' ? s : `R ${s}`; },
  text: (v) => (v === null || v === undefined || v === '' ? '—' : String(v)),
  number: (v) => (v === null || v === undefined || v === '' ? '—' : String(v))
};
const fmt = (tag, v) => (FORMATTERS[tag] || FORMATTERS.text)(v);

// Retire's fields, named once and shared by the internal `retire` action and by
// the three enum values the backend actually stores, so a retirement reads the
// same on the timeline however it was written.
//
// There is deliberately no sale-value field: the reports treat
// serviceDeliveryCost as money spent, and posting sale proceeds into it would
// quietly corrupt every cost total. Recovered value wants its own field on the
// backend before it belongs on this form.
const RETIRE_FIELDS = [
  {
    key: 'retireReason', label: 'Reason', format: 'text',
    input: {
      control: 'choice', required: true,
      options: RETIRE_REASONS.map((r) => ({ value: r.id, label: r.label, hint: r.hint }))
    }
  },
  { key: 'notes', label: 'Notes', format: 'text', input: { control: 'textarea' } }
];

// Read-only display fields shared by the retread-batch actions (retread_dispatch /
// retread_return). These entries are stamped by the backend, never captured on a
// form, so there is no `input`. `reference` is the human batch code (RT-…);
// `rejectReason` only appears on a rejected/written-off return. The retread
// measurements the receive form sends (tread / grade / cost) surface via the
// generic pass in getActionDetailRows if present, and the internal ids
// (retreadBatchId / supplierId / operation envelope) and `outcome` — which drives
// the entry label — are reserved off the timeline (see RESERVED_DETAIL_KEYS).
// The exact key names the backend writes into this bag are worth a quick confirm
// against a real record; unknown-but-meaningful keys still print (humanized).
const RETREAD_BATCH_FIELDS = [
  { key: 'reference', label: 'Batch', format: 'text' },
  { key: 'rejectReason', label: 'Reason', format: 'text' },
  { key: 'notes', label: 'Notes', format: 'text' }
];
// things from one definition: the form inputs (input), the details bag written
// to the log (buildActionDetails), the backend field name it is posted under on
// POST /action (record → buildActionPayloadFields) and the timeline display
// (format via getActionDetailRows).
export const ACTION_META = Object.freeze({
  [TYRE_ACTION.FIT]: {
    label: 'Fitted', verb: 'Fit', icon: 'fit', tone: 'fit',
    detailFields: [
      { key: 'application', label: 'Application', format: 'text' },
      { key: 'notes', label: 'Notes', format: 'text' }
    ]
  },
  [TYRE_ACTION.REMOVE]: {
    label: 'Removed', verb: 'Remove', icon: 'fit', tone: 'slate',
    detailFields: [
      { key: 'reason', label: 'Reason', format: 'text' },
      { key: 'notes', label: 'Notes', format: 'text' }
    ]
  },
  [TYRE_ACTION.INSPECT]: {
    label: 'Inspected', verb: 'Inspect', icon: 'inspect', tone: 'inspect',
    logMessage: 'Inspection logged',
    detailFields: [
      {
        key: 'treadDepth', label: 'Tread depth', format: 'mm', record: 'tyreTreadDepth',
        input: { control: 'number', step: '0.01', min: '0', unit: 'mm', required: true }
      },
      {
        key: 'wearPattern', label: 'Wear pattern', format: 'text', record: 'tyreWearPattern',
        input: { control: 'select', options: WEAR_PATTERNS }
      },
      {
        key: 'damageSeverity', label: 'Damage', format: 'text', record: 'tyreDamageSeverity',
        input: { control: 'select', options: TYRE_DAMAGE_SEVERITIES }
      },
      { key: 'notes', label: 'Notes', format: 'text', input: { control: 'textarea' } }
    ]
  },
  [TYRE_ACTION.ROTATE]: {
    label: 'Rotated', verb: 'Rotate', icon: 'rotate', tone: 'amber',
    logMessage: 'Rotation logged',
    // No `input` on any field: a rotation is logged one-shot from the tyre's
    // detail screen with only a date. The tyre keeps its sensor; the backend
    // records the from/to wheel positions and on-truck distance itself, so these
    // are display-only — they render on the timeline if the server sets them.
    detailFields: [
      { key: 'fromPosition', label: 'From', format: 'text' },
      { key: 'toPosition', label: 'To', format: 'text' },
      { key: 'reason', label: 'Reason', format: 'text' },
      { key: 'notes', label: 'Notes', format: 'text' }
    ]
  },
  [TYRE_ACTION.REPAIR]: {
    label: 'Repaired', verb: 'Repair', icon: 'repair', tone: 'repair',
    logMessage: 'Repair logged',
    detailFields: [
      {
        key: 'repairType', label: 'Repair type', format: 'text', record: 'tyreRepairType',
        input: { control: 'select', options: TYRE_REPAIR_TYPES, required: true }
      },
      {
        key: 'damageSeverity', label: 'Damage', format: 'text', record: 'tyreDamageSeverity',
        input: { control: 'select', options: TYRE_DAMAGE_SEVERITIES }
      },
      {
        key: 'serviceDelivery', label: 'Service', format: 'text',
        input: { control: 'select', options: SERVICE_DELIVERY_OPTIONS }
      },
      {
        key: 'serviceDeliveryCost', label: 'Cost', format: 'money',
        input: { control: 'number', step: '0.01', min: '0', prefix: 'R' }
      },
      {
        key: 'treadDepth', label: 'Tread depth', format: 'mm', record: 'tyreTreadDepth',
        input: { control: 'number', step: '0.01', min: '0', unit: 'mm' }
      },
      { key: 'notes', label: 'Notes', format: 'text', input: { control: 'textarea' } }
    ]
  },
  [TYRE_ACTION.RETREAD]: {
    label: 'Retreaded', verb: 'Retread', icon: 'retread', tone: 'retread',
    logMessage: 'Retread logged',
    detailFields: [
      {
        key: 'grade', label: 'Grade', format: 'text', record: 'tyreGrade',
        input: { control: 'select', options: TYRE_GRADES }
      },
      {
        key: 'initialTreadDepth', label: 'Initial tread', format: 'mm', record: 'initialTyreTreadDepth',
        input: { control: 'number', step: '0.01', min: '0', unit: 'mm' }
      },
      {
        key: 'treadDepth', label: 'Tread depth', format: 'mm', record: 'tyreTreadDepth',
        input: { control: 'number', step: '0.01', min: '0', unit: 'mm' }
      },
      {
        key: 'serviceDelivery', label: 'Service', format: 'text',
        input: { control: 'select', options: SERVICE_DELIVERY_OPTIONS }
      },
      {
        key: 'serviceDeliveryCost', label: 'Cost', format: 'money',
        input: { control: 'number', step: '0.01', min: '0', prefix: 'R' }
      },
      { key: 'notes', label: 'Notes', format: 'text', input: { control: 'textarea' } }
    ]
  },
  [TYRE_ACTION.RETIRE]: {
    label: 'Retired', verb: 'Retire', icon: 'retire', tone: 'slate',
    logMessage: 'Retirement logged',
    detailFields: RETIRE_FIELDS
  },

  // READ side. The backend stores scrap / sale / missing and never 'retire', so
  // these are what actually come back on the history. They are deliberately NOT
  // aliased onto `retire`: the timeline should say what happened rather than make
  // the reader open the details bag to find out which kind of leaving it was.
  scrap: { label: 'Scrapped', verb: 'Retire', icon: 'retire', tone: 'slate', detailFields: RETIRE_FIELDS },
  sale: { label: 'Sold', verb: 'Retire', icon: 'retire', tone: 'slate', detailFields: RETIRE_FIELDS },
  missing: { label: 'Missing', verb: 'Retire', icon: 'retire', tone: 'red', detailFields: RETIRE_FIELDS },

  // Also a terminal leaving, kept beside scrap / sale / missing so a standalone
  // write-off renders as an event, not a raw key. NOTE the retread REJECT and
  // WRITE-OFF outcomes do NOT arrive as this action — they come back as a single
  // `retread_return` carrying details.outcome and the final status, and are
  // rendered from there (see below). This entry covers a write-off logged outside
  // the retread flow.
  write_off: { label: 'Written off', verb: 'Retire', icon: 'retire', tone: 'slate', detailFields: RETIRE_FIELDS },

  // ---- retread batch (backend-stamped; read-only, like scrap/sale/missing) -----
  // A tyre sent out on a batch. `details.outcome` and the batch identity are the
  // interesting bits; the internal ids are reserved off the timeline (below).
  retread_dispatch: {
    label: 'Sent for retread', verb: 'Dispatch', icon: 'retread', tone: 'retread',
    detailFields: RETREAD_BATCH_FIELDS
  },
  // The return. resolveActionType maps the outcome onto one of the three labelled
  // variants; getActionDetailRows reads this base key, so all four share fields.
  // The base entry is the fallback when an outcome is missing/unknown.
  retread_return: { label: 'Returned from retread', verb: 'Receive', icon: 'retread', tone: 'retread', detailFields: RETREAD_BATCH_FIELDS },
  retread_return_retreaded: { label: 'Retreaded', verb: 'Receive', icon: 'retread', tone: 'fit', detailFields: RETREAD_BATCH_FIELDS },
  retread_return_rejected: { label: 'Rejected', verb: 'Receive', icon: 'retire', tone: 'red', detailFields: RETREAD_BATCH_FIELDS },
  retread_return_written_off: { label: 'Written off', verb: 'Receive', icon: 'retire', tone: 'slate', detailFields: RETREAD_BATCH_FIELDS }
});

// Actions captured on the shared Action Capture form (multi-field data entry).
// Fit / remove are association-driven (sensor/wheel picker + 409 handling) and
// live on their own form. Rotate is a one-shot logged inline from the Inspect
// form (date only, fitted tyres) — so none of those three belong in this list.
//
// Retire is last on purpose: this array orders the detail screen's action bar,
// and the one irreversible action belongs at the end of it.
// The actions a user can log from a tyre's detail page. Single-tyre RETREAD was
// removed here on purpose — retreads now go through retread batches, not per tyre —
// but TYRE_ACTION.RETREAD and its ACTION_META entry stay defined so historical
// single-tyre retreads still render on the timeline. Add it back here to re-enable
// the per-tyre action.
export const FORM_ACTION_TYPES = [
  TYRE_ACTION.INSPECT, TYRE_ACTION.REPAIR, TYRE_ACTION.RETIRE
];

export const ACTION_TYPES = Object.keys(ACTION_META);

const isBlank = (v) => v === null || v === undefined || v === '';

export function actionLabel(actionType) {
  const t = normalizeActionType(actionType);
  return ACTION_META[t]?.label || (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Action');
}
export function actionIcon(actionType) { return ACTION_META[normalizeActionType(actionType)]?.icon || 'tyre'; }
export function actionTone(actionType) { return ACTION_META[normalizeActionType(actionType)]?.tone || 'slate'; }
export function actionVerb(actionType) {
  const t = normalizeActionType(actionType);
  return ACTION_META[t]?.verb || (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Action');
}
export function actionLogMessage(actionType) {
  const t = normalizeActionType(actionType);
  return ACTION_META[t]?.logMessage || `${actionVerb(t)} logged`;
}

// The field definitions the capture form should render for an action.
export function getActionFields(actionType) {
  return ACTION_META[normalizeActionType(actionType)]?.detailFields || [];
}

// WRITE side: assemble the details bag for an action from a flat values object.
// Only that action's known keys are included, and blanks are dropped, so the
// stored object stays tight and predictable.
export function buildActionDetails(actionType, values = {}) {
  const t = normalizeActionType(actionType);
  const fields = ACTION_META[t]?.detailFields || [];
  const details = {};
  for (const f of fields) {
    const v = values[f.key];
    if (isBlank(v)) continue;
    if (f.format === 'mm') {
      const n = Number(v);
      details[f.key] = Math.round((n + Number.EPSILON) * 100) / 100;
    } else {
      details[f.key] = (f.format === 'money' || f.format === 'number') ? Number(v) : v;
    }
  }
  return details;
}

// WRITE side: the POST /action body fields. The backend updates the tyre record
// from these top-level fields (tyreTreadDepth, tyreRepairType, serviceDeliveryCost,
// …) and builds the stored details bag itself; a nested `details` object is
// ignored. Fields with a `record` mapping go out under that backend name; the rest
// (serviceDelivery, serviceDeliveryCost, notes) already match. Only filled-in
// fields are sent — omitted ones are left untouched on the record.
// getActionDetailRows reads either name back.
export function buildActionPayloadFields(actionType, values = {}) {
  const t = normalizeActionType(actionType);
  const fields = ACTION_META[t]?.detailFields || [];
  const details = buildActionDetails(t, values);
  const payload = {};
  for (const f of fields) {
    if (f.key in details) payload[f.record || f.key] = details[f.key];
  }
  return payload;
}

function humanizeKey(key) {
  return String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

// Keys the server folds into the details bag that must stay OFF the timeline: the
// derived status SNAPSHOT, plus the log-envelope identity and the association
// snapshot.
//
// The tyreStatus snapshot is the one that bit us — it is where "Tyre Status:
// UNDER_REPAIR" came from. UNDER_REPAIR is an ACTION, never a status (see
// tyre-status.js), so echoing the stamp painted an impossible status onto the
// history. A tyre's status is derived and shown once, in the header badge
// (effectiveStatus); the timeline answers "what did we do", not "what state does
// the server think it's in". The association fields (sensor / wheel / unit) are
// already drawn by the row's own position chip and label (positionChange /
// resolveActionType), so echoing them again only duplicates.
//
// Deliberately NOT reserved: `action` — the server's human sentence for the entry
// (e.g. "Tyre 4282 was associated to sensor 904058589"). That narrative is wanted
// on the timeline, so it surfaces as a row alongside `reason`; getActionDetailRows
// de-duplicates by value, so if the server sends `action` and `reason` with the
// same text the sentence still prints only once.
//
// This filters a FIXED envelope, not "anything unfamiliar" — a genuine detail
// another client writes into the bag still surfaces, so the forward-compat
// promise below holds for real data while the server's bookkeeping stays off the
// timeline.
const RESERVED_DETAIL_KEYS = new Set([
  // status snapshots — derived and shown in the header, never per event
  'tyreStatus', 'status', 'effectiveStatus',
  // log envelope / identity — note: `action` (the human narrative) is intentionally
  // absent here, so it is shown on the timeline
  'actionType', 'timestamp', 'createdAt', 'updatedAt',
  'createdBy', 'updatedBy', 'id', 'tyreId', 'externalTyreId', 'customerId',
  // association snapshot — already rendered by the timeline's position chip + label
  'currentSensorId', 'previousSensorId', 'currentWheelId', 'previousWheelId',
  'unitId', 'unitName', 'unitType', 'imeiNumber', 'yardId', 'yardName',
  // retread batch: `outcome` drives the entry label (resolveActionType) so it isn't
  // repeated as a row; the ids/operation envelope are internal bookkeeping (the
  // human batch code surfaces via the `reference` field instead)
  'outcome', 'retreadBatchId', 'retreadBatchLineId', 'supplierId',
  'operationId', 'operationType', 'operationState', 'operationError'
]);

// READ side: turn an action-log entry's details into display rows. Known fields
// (in schema order) come first; any extra keys another client added are still
// shown generically, so nothing MEANINGFUL in the bag is ever silently dropped —
// only the reserved envelope above is filtered.
export function getActionDetailRows(actionLog = {}) {
  const t = normalizeActionType(actionLog.actionType);
  const details = actionLog.details && typeof actionLog.details === 'object' ? actionLog.details : {};
  const fields = ACTION_META[t]?.detailFields || [];
  // A field may come back under its details key or its backend record name
  // (e.g. repairType / tyreRepairType) — see buildActionPayloadFields.
  const known = new Set(fields.flatMap((f) => (f.record ? [f.key, f.record] : [f.key])));
  const valueOf = (f) => (isBlank(details[f.key]) && f.record ? details[f.record] : details[f.key]);

  // Track rendered values so a generic key never repeats a sentence a named field
  // already showed — the case that matters is a server sending both `action` and
  // `reason` with identical text. Known fields always emit (schema data is never
  // dropped); only the generic pass skips a value already on screen.
  const seen = new Set();
  const norm = (s) => String(s).trim().toLowerCase();

  const rows = [];
  for (const f of fields) {
    const raw = valueOf(f);
    if (isBlank(raw)) continue;
    const value = fmt(f.format, raw);
    seen.add(norm(value));
    rows.push({ label: f.label, value });
  }
  for (const key of Object.keys(details)) {
    if (known.has(key) || RESERVED_DETAIL_KEYS.has(key) || isBlank(details[key])) continue;
    const v = details[key];
    const value = typeof v === 'object' ? JSON.stringify(v) : String(v);
    if (seen.has(norm(value))) continue;   // don't repeat a sentence already shown (e.g. action == reason)
    seen.add(norm(value));
    rows.push({ label: humanizeKey(key), value });
  }
  return rows;
}

// The sensor/position move lives at the TOP LEVEL of the action log (not in
// details); format it once here for the timeline. Returns null when unchanged.
export function positionChange(actionLog = {}, formatPosition = (x) => (x == null ? '' : String(x))) {
  const from = actionLog.previousWheelId;
  const to = actionLog.currentWheelId;
  if (isBlank(from) && isBlank(to)) return null;
  const f = isBlank(from) ? '—' : (formatPosition(from) || String(from));
  const tt = isBlank(to) ? '—' : (formatPosition(to) || String(to));
  if (f === tt) return null;
  return { from: f, to: tt };
}
